import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { friendlyPastTime } from '../../lib/friendlyTime';
import { RuleHomesList, usePolicyChoices, changeLabel } from '../PPM/RuleTakeOn';

// ===========================================================================
// RuleIQ, laid out like an Asana project. A left sidebar lists the rules.
// A rule opens as a project: header with icon, name, status and the
// Download PDF action; views Overview, Where the work went, Ask the rule,
// Reports. A task pane opens beside the list. Every action created from the
// rule (work for a department, an issues log entry, a risk) shows in the
// list with its live status. No engineering words on screen.
// ===========================================================================

const API = process.env.REACT_APP_API_URL || '';
const j = (url, opts) => fetch(`${API}${url}`, { credentials: 'include', ...opts }).then((r) => r.json());
const post = (url, body) => j(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

function isComplianceLevel(user) {
  const ut = user?.user_type || user?.effective_user_type || null;
  if (ut) return ['platform_superuser', 'compliance_officer', 'compliance_analyst'].includes(ut);
  return ['superadmin'].includes(user?.role) || (user?.role === 'admin' && !user?.department_id);
}

const STEP_LABEL = {
  pass_0_finalization: 'Reading the regulation text', pass_amendatory: 'Reading the regulation text',
  pass_preamble_only: 'Reading what CMS decided', pass_6_comment_intelligence: 'Reading the public comments',
  pass_3_gaps: 'Organizing by department', pass_4_intelligence: 'Organizing by department', title_polish: 'Organizing by department', department_mapping: 'Organizing by department',
  agent_integration: 'Sharing with the policy app', rule_summary: 'Writing the summary',
};
const stepOf = (a) => !a ? '' : (a.status === 'uploading' || a.status === 'extracting') ? 'Opening the rule' : a.status === 'generating' ? 'Preparing the reports' : (STEP_LABEL[a.current_pass] || 'Reading the rule');
const isDone = (a) => a?.status === 'completed';
const isOver = (a) => ['completed', 'failed', 'cancelled'].includes(a?.status);
const baseOf = (s) => (String(s || '').match(/\b(\d{2,3}\.\d+)/) || [])[1] || null;
const cleanTitle = (t) => String(t || '').replace(/^Not finalized:\s*/i, '');
// A date only value (due dates) is shown as the calendar day it names; a
// timestamp is shown in the viewer's time zone.
const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  const dayOnly = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/.test(s);
  const dt = dayOnly ? new Date(`${s.slice(0, 10)}T00:00:00`) : new Date(s);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const ruleColor = (id) => ['#f06a6a', '#ec8d71', '#f1bd6c', '#aecf55', '#5da283', '#4ecbc4', '#4573d2', '#8d84e8', '#b36bd4', '#f9aaef'][[...String(id || '')].reduce((h, c) => (h + c.charCodeAt(0)) % 10, 0)];

// ---------------------------------------------------------------------------
// Asana vocabulary: chips, avatars, buttons, check circle
// ---------------------------------------------------------------------------
const CHIP = {
  green: 'bg-emerald-100 text-emerald-800', yellow: 'bg-amber-100 text-amber-800', red: 'bg-rose-100 text-rose-800',
  blue: 'bg-sky-100 text-sky-800', purple: 'bg-violet-100 text-violet-800', gray: 'bg-slate-100 text-slate-700', orange: 'bg-orange-100 text-orange-800', teal: 'bg-teal-100 text-teal-800',
};
const Chip = ({ tone = 'gray', children, className = '' }) => <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap leading-5 ${CHIP[tone] || CHIP.gray} ${className}`}>{children}</span>;
const AVATAR = ['bg-rose-200 text-rose-900', 'bg-sky-200 text-sky-900', 'bg-emerald-200 text-emerald-900', 'bg-violet-200 text-violet-900', 'bg-amber-200 text-amber-900', 'bg-teal-200 text-teal-900'];
function Avatar({ name, size = 6, className = '' }) {
  const n = String(name || '').trim();
  const box = size === 8 ? 'w-8 h-8 text-xs' : size === 7 ? 'w-7 h-7 text-[11px]' : 'w-6 h-6 text-[10px]';
  if (!n) return <span className={`${box} rounded-full border border-dashed border-slate-300 inline-flex items-center justify-center text-slate-300 ${className}`} title="No owner yet">·</span>;
  const initials = n.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const color = AVATAR[[...n].reduce((h, c) => (h + c.charCodeAt(0)) % AVATAR.length, 0)];
  return <span title={n} className={`${box} rounded-full inline-flex items-center justify-center font-semibold shrink-0 ${color} ${className}`}>{initials}</span>;
}
const btn = {
  primary: 'inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition',
  secondary: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 transition',
  ghost: 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 transition',
};
const field = 'w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm text-slate-800 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
function CheckCircle({ done, onClick, disabled }) {
  const cls = done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500';
  return <button type="button" disabled={disabled || !onClick} onClick={(e) => { e.stopPropagation(); onClick?.(); }} className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center text-[10px] shrink-0 ${cls} ${!onClick ? 'cursor-default' : ''}`}>✓</button>;
}
const Empty = ({ children }) => <div className="text-sm text-slate-500 py-8 px-1">{children}</div>;

// ---------------------------------------------------------------------------
// App shell: sidebar of rules + the selected rule as a project
// ---------------------------------------------------------------------------
export default function RuleIQ({ user = null }) {
  const canWork = isComplianceLevel(user);
  const [rules, setRules] = useState(null);
  const [ruleId, setRuleId] = useState(null);
  const [newRule, setNewRule] = useState(false);
  const load = useCallback(async () => { try { const d = await j('/api/priq/analyses'); if (d.success) setRules(d.analyses || []); else setRules([]); } catch { setRules([]); } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('priq_id') || q.get('analysis');
    if (id) setRuleId(id);
  }, []);
  useEffect(() => { if (rules && rules.length && !ruleId && !newRule) setRuleId(rules[0].id); }, [rules, ruleId, newRule]);

  return (
    <div className="absolute inset-0 flex bg-white">
      <Sidebar rules={rules} ruleId={newRule ? null : ruleId} canWork={canWork} onPick={(id) => { setNewRule(false); setRuleId(id); }} onNew={() => setNewRule(true)} />
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {newRule ? <NewRule onBack={() => setNewRule(false)} onCreated={(id) => { setNewRule(false); load(); setRuleId(id); }} />
          : ruleId ? <RuleProject key={ruleId} ruleId={ruleId} canWork={canWork} onChanged={load} />
          : rules && rules.length === 0 ? <Welcome canWork={canWork} onNew={() => setNewRule(true)} />
          : <Empty>Loading…</Empty>}
      </div>
    </div>
  );
}

function Sidebar({ rules, ruleId, canWork, onPick, onNew }) {
  return (
    <aside className="w-60 shrink-0 bg-[#f9f8f8] border-r border-slate-200 flex flex-col">
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <div className="text-[13px] font-semibold text-slate-900">RuleIQ</div>
        {canWork && <button onClick={onNew} className="w-6 h-6 rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-900 text-lg leading-none" title="Read a new rule">+</button>}
      </div>
      <div className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Rules</div>
      <nav className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5">
        {!rules ? <div className="px-2 text-xs text-slate-400">Loading…</div> : rules.map((r) => (
          <button key={r.id} onClick={() => onPick(r.id)} className={`w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2.5 text-[13px] transition ${ruleId === r.id ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-700 hover:bg-slate-200/60'}`}>
            <span className="w-3 h-3 rounded-[4px] shrink-0" style={{ background: ruleColor(r.id) }} />
            <span className="truncate flex-1">{r.title}</span>
            {!isOver(r) && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shrink-0" title={stepOf(r)} />}
          </button>
        ))}
      </nav>
      {canWork && <div className="px-3 py-3 border-t border-slate-200"><button onClick={onNew} className={btn.secondary + ' w-full justify-center'}>Read a new rule</button></div>}
    </aside>
  );
}

function Welcome({ canWork, onNew }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center text-xl font-bold mx-auto">§</div>
        <h1 className="text-lg font-semibold text-slate-900 mt-4">No rules yet</h1>
        <p className="text-sm text-slate-500 mt-1">Read a CMS final rule and it becomes a summary, work for your departments, and a shared record across the platform.</p>
        {canWork && <button onClick={onNew} className={btn.primary + ' mt-5'}>Read a new rule</button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New rule
// ---------------------------------------------------------------------------
function NewRule({ onBack, onCreated }) {
  const [file, setFile] = useState(null);
  const [year, setYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(false);
  const pick = () => { const i = document.createElement('input'); i.type = 'file'; i.accept = '.pdf'; i.onchange = (e) => { if (e.target.files[0]) setFile(e.target.files[0]); }; i.click(); };
  const start = async () => {
    if (!file || busy) return;
    setBusy(true); setErr(null);
    try {
      if ('Notification' in window && Notification.permission === 'default') { try { await Notification.requestPermission(); } catch { /* ignore */ } }
      const form = new FormData(); form.append('current_year_pdf', file); if (year.trim()) form.append('current_year', year.trim());
      const res = await fetch(`${API}/api/priq/analyses`, { method: 'POST', credentials: 'include', body: form });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'The rule could not be opened.');
      onCreated(d.analysis?.id || d.id);
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-xl mx-auto px-8 py-12">
        <button onClick={onBack} className={btn.ghost + ' -ml-2 mb-6'}>← Back</button>
        <h1 className="text-xl font-semibold text-slate-900">Read a new rule</h1>
        <p className="text-sm text-slate-500 mt-1">Upload the final rule as published in the Federal Register. Reading takes about an hour; you will be told when it is ready.</p>
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); }} onClick={pick}
          className={`mt-6 rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition ${drag ? 'border-orange-400 bg-orange-50/40' : 'border-slate-300 hover:border-orange-300'}`}>
          {file ? <><div className="text-[15px] font-medium text-slate-900">{file.name}</div><div className="text-xs text-slate-400 mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB · click to choose another</div></>
            : <><div className="text-[15px] font-medium text-slate-800">Drop the rule here</div><div className="text-xs text-slate-400 mt-1">PDF, or click to choose</div></>}
        </div>
        <label className="block text-xs font-medium text-slate-500 mt-5 mb-1.5">Contract year <span className="font-normal text-slate-400">(optional)</span></label>
        <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="Read from the rule when left blank" className={field} />
        {err && <p className="text-sm text-rose-600 mt-3">{err}</p>}
        <button onClick={start} disabled={!file || busy} className={btn.primary + ' mt-6'}>{busy ? 'Opening the rule…' : 'Read the rule'}</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The rule as a project
// ---------------------------------------------------------------------------
function RuleProject({ ruleId, canWork, onChanged }) {
  const [a, setA] = useState(null);
  const [findings, setFindings] = useState([]);
  const [c, setC] = useState(null);
  const [reports, setReports] = useState(null);
  const [view, setView] = useState('overview');
  const [pane, setPane] = useState(null); // { type: 'item', item } | { type: 'create', draft }
  const [error, setError] = useState(null);

  const loadRule = useCallback(async () => { try { const d = await j(`/api/priq/analyses/${ruleId}`); if (d.success) setA(d.analysis); else setError(d.message || 'Not found'); } catch { setError('Could not load this rule.'); } }, [ruleId]);
  const loadC = useCallback(async () => { try { const d = await j(`/api/priq/analyses/${ruleId}/connections`); if (d.success) setC(d); } catch { /* keep */ } }, [ruleId]);
  // What the rule requires of the organization and where each requirement
  // lands on the policies (read and judged once per policy version).
  const [cov, setCov] = useState(null);
  const loadCov = useCallback(async () => { try { const d = await j(`/api/priq/analyses/${ruleId}/coverage`); if (d.success) setCov(d); } catch { /* keep */ } }, [ruleId]);
  useEffect(() => { if (isDone(a)) loadCov(); }, [a, loadCov]);
  const loadReports = useCallback(async () => { try { const d = await j(`/api/priq/analyses/${ruleId}/reports`); setReports(d.success ? d.reports || [] : []); } catch { setReports([]); } }, [ruleId]);
  useEffect(() => { loadRule(); }, [loadRule]);
  useEffect(() => { if (!a || isOver(a)) return undefined; const t = setInterval(loadRule, 4000); return () => clearInterval(t); }, [a, loadRule]);
  useEffect(() => { if (!isDone(a)) return; j(`/api/priq/analyses/${ruleId}/findings`).then((d) => { if (d.success) setFindings(d.findings || []); }).catch(() => {}); loadC(); loadReports(); }, [a, ruleId, loadC, loadReports]);

  const decisions = useMemo(() => findings.filter((f) => f.pass === 'pass_preamble_only'), [findings]);
  const codified = useMemo(() => findings.filter((f) => f.pass === 'pass_amendatory'), [findings]);
  const byId = useMemo(() => new Map(findings.map((f) => [f.id, f])), [findings]);
  const rs = a?.summary?.rule_summary || null;
  const mainReport = (reports || []).find((r) => r.report_type === 'comprehensive' && r.status === 'completed');

  const download = async (r) => {
    if (!r) return;
    try { const res = await fetch(`${API}/api/priq/analyses/${ruleId}/reports/${r.id}/download`, { credentials: 'include' }); if (!res.ok) return; const url = URL.createObjectURL(await res.blob()); const el = document.createElement('a'); el.href = url; el.download = r.file_name || 'report.pdf'; el.click(); URL.revokeObjectURL(url); } catch { /* ignore */ }
  };
  const openCreate = (draft) => setPane({ type: 'create', draft: draft || {} });

  if (error) return <Empty>{error}</Empty>;
  if (!a) return <Empty>Loading…</Empty>;

  const status = isDone(a) ? { label: 'Ready', dot: 'bg-emerald-500' } : a.status === 'failed' ? { label: 'Could not finish', dot: 'bg-rose-500' } : a.status === 'cancelled' ? { label: 'Stopped', dot: 'bg-slate-400' } : { label: stepOf(a), dot: 'bg-orange-500 animate-pulse' };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <header className="px-8 pt-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg text-white flex items-center justify-center text-base font-bold shrink-0" style={{ background: ruleColor(ruleId) }}>§</span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-slate-900 truncate">{a.title}</h1>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
              <span className={`w-2 h-2 rounded-full ${status.dot}`} /><span>{status.label}</span>
              <span>·</span><span>{a.current_year || 'CMS'} final rule</span>
              <span>·</span><span>read {friendlyPastTime(a.completed_at || a.created_at)}{a.created_by_name ? ` by ${a.created_by_name}` : ''}</span>
            </div>
          </div>
          {isDone(a) && (
            <div className="flex items-center gap-2 shrink-0">
              {canWork && <button onClick={() => openCreate({})} className={btn.secondary}>+ Action</button>}
              <button onClick={() => download(mainReport)} disabled={!mainReport} className={btn.primary}>Download PDF</button>
            </div>
          )}
        </div>
        {isDone(a) && (
          <nav className="flex gap-1 mt-4 -mb-px">
            {[['overview', 'Overview'], ['changes', 'All changes'], ['policies', 'Policies'], ['work', 'Where the work went'], ...(canWork ? [['ask', 'Ask the rule']] : []), ['reports', 'Reports']].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} className={`px-3 py-2 text-sm border-b-2 transition ${view === id ? 'border-slate-900 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{label}</button>
            ))}
          </nav>
        )}
      </header>

      <div className="flex-1 min-h-0 flex">
        <main className="flex-1 min-w-0 overflow-y-auto">
          {!isDone(a) ? <Reading a={a} canWork={canWork} onStopped={() => { loadRule(); onChanged(); }} />
            : view === 'overview' ? <Overview a={a} rs={rs} byId={byId} decisions={decisions} codified={codified} c={c} cov={cov} reports={reports} canWork={canWork} onView={setView} onDownload={download} onSummaryWritten={loadRule} onOpen={(item) => setPane({ type: 'item', item })} />
            : view === 'changes' ? <Changes decisions={decisions} codified={codified} rs={rs} />
            : view === 'policies' ? <PoliciesView ruleId={ruleId} cov={cov} canWork={canWork} onChanged={loadCov} />
            : view === 'work' ? <WorkList c={c} canWork={canWork} onOpen={(item) => setPane({ type: 'item', item })} onCreate={openCreate} />
            : view === 'ask' ? <Ask ruleId={ruleId} onCreate={openCreate} />
            : <Reports reports={reports} canWork={canWork} ruleId={ruleId} onDownload={download} onRebuilt={loadReports} />}
        </main>
        {pane && (
          pane.type === 'create'
            ? <CreatePane ruleId={ruleId} draft={pane.draft} onClose={() => setPane(null)} onCreated={(item) => { loadC(); setPane({ type: 'item', item }); }} />
            : <ItemPane item={pane.item} ruleId={ruleId} canWork={canWork} onClose={() => setPane(null)} onChanged={loadC} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Policies: what the rule requires of the organization, which policy covers
// each requirement, and where the uncovered ones should go. Compliance can
// assign a home; the requirement then appears on that policy's review.
// ---------------------------------------------------------------------------
function PoliciesView({ ruleId, cov, canWork, onChanged }) {
  const policies = usePolicyChoices(!!canWork);
  const [refreshing, setRefreshing] = useState(false);
  const [showNot, setShowNot] = useState(false);
  const [showCovered, setShowCovered] = useState(false);
  if (!cov) return <Empty>Loading…</Empty>;
  const homes = cov.homes || [];
  const not = cov.not_requirements || [];
  const covered = homes.filter((h) => h.status === 'covered' && !h.decided);
  const openItems = homes.filter((h) => h.status !== 'covered' || h.decided);
  const refresh = async () => {
    setRefreshing(true);
    try { await post(`/api/priq/analyses/${ruleId}/coverage/refresh`); } catch { /* ignore */ }
    setTimeout(() => { setRefreshing(false); onChanged(); }, 4000);
  };
  const NOT_LABEL = { editorial: 'Wording, numbering or cross reference only', definition: 'Definitions only', other_actor: 'A duty on CMS, a State or a manufacturer, not the plan' };
  return (
    <div className="px-8 py-6 max-w-4xl pb-16">
      {homes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-5">
          <p className="text-sm text-slate-600">This rule has not been placed on the policies yet. Placing it reads every regulation change for what the organization must do, then judges which policies cite or govern each one.</p>
          {canWork && <button onClick={refresh} disabled={refreshing} className={btn.primary + ' mt-3'}>{refreshing ? 'Placing… (a few minutes)' : 'Place this rule on the policies'}</button>}
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-[15px] font-semibold text-slate-900">What no policy covers yet</h2>
              <p className="text-sm text-slate-500 mt-0.5">{homes.length} requirement{homes.length === 1 ? '' : 's'} the organization owes under this rule. {covered.length} covered by a policy that cites the section or governs the subject. {openItems.length} listed below.</p>
            </div>
            {canWork && <button onClick={refresh} disabled={refreshing} className={btn.secondary}>{refreshing ? 'Placing…' : 'Place again'}</button>}
          </div>
          <div className="mt-4">
            <RuleHomesList homes={homes} canDecide={!!canWork} policies={policies}
              decideUrl={(h) => `${API}/api/priq/analyses/${ruleId}/coverage/${h.id}/decide`} onChanged={onChanged}
              emptyText="Every requirement of this rule is covered by a policy." />
          </div>

          <div className="mt-8">
            <button onClick={() => setShowCovered((v) => !v)} className="text-sm text-slate-700 font-medium hover:underline">{showCovered ? 'Hide' : 'Show'} the {covered.length} covered by a policy</button>
            {showCovered && (
              <ul className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
                {covered.map((h) => (
                  <li key={h.id} className="py-3">
                    <div className="text-sm text-slate-900">{h.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{changeLabel(h)} · 42 CFR {h.cfr_section} · {(h.covered_by || []).length ? `covered by ${h.covered_by.map((p) => `${p.policy_number} ${p.title}`).join('; ')}` : (h.kind === 'removed' ? 'no policy cites this section, so nothing relies on the removed text' : 'covered')}</div>
                    {h.plain && <p className="text-sm text-slate-600 mt-1">{h.plain}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {not.length > 0 && (
            <div className="mt-6">
              <button onClick={() => setShowNot((v) => !v)} className="text-sm text-slate-700 font-medium hover:underline">{showNot ? 'Hide' : 'Show'} the {not.length} regulation change{not.length === 1 ? '' : 's'} that ask nothing of a policy</button>
              {showNot && (
                <ul className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
                  {not.map((r) => (
                    <li key={r.id} className="py-3">
                      <div className="text-sm text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">42 CFR {r.cfr_section} · {NOT_LABEL[r.kind] || r.kind}{r.reason ? ` · ${r.reason}` : ''}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Reading({ a, canWork, onStopped }) {
  const steps = ['Opening the rule', 'Reading the regulation text', 'Reading what CMS decided', 'Reading the public comments', 'Organizing by department', 'Writing the summary', 'Preparing the reports'];
  const idx = Math.max(0, steps.indexOf(stepOf(a)));
  const stop = async () => { try { await post(`/api/priq/analyses/${a.id}/cancel`); onStopped(); } catch { /* ignore */ } };
  if (a.status === 'failed' || a.status === 'cancelled') return <div className="px-8 py-10 max-w-xl"><div className="text-[15px] font-medium text-slate-900">{a.status === 'failed' ? 'This rule could not be read to the end.' : 'Reading was stopped.'}</div>{a.error_message && <p className="text-sm text-slate-500 mt-1">{a.error_message}</p>}</div>;
  return (
    <div className="px-8 py-10 max-w-xl">
      <div className="flex items-center justify-between"><div className="text-[15px] font-medium text-slate-900">Reading the rule</div><span className="text-xs text-slate-400 tabular-nums">{a.progress_pct || 0}%</span></div>
      <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500 transition-all duration-700" style={{ width: `${a.progress_pct || 0}%` }} /></div>
      <ol className="mt-6 space-y-2.5">{steps.map((st, i) => <li key={st} className={`flex items-center gap-3 text-sm ${i === idx ? 'text-slate-900 font-medium' : 'text-slate-400'}`}><CheckCircle done={i < idx} />{st}</li>)}</ol>
      <p className="text-sm text-slate-500 mt-6">About an hour for a full rule. You can leave this page; you will be told when it is ready.</p>
      {canWork && <button onClick={stop} className={btn.secondary + ' mt-4'}>Stop reading</button>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview: the summary as the project description, plus the side column
// ---------------------------------------------------------------------------
function Overview({ a, rs, byId, decisions, codified, c, cov, reports, canWork, onView, onDownload, onSummaryWritten, onOpen }) {
  const [writing, setWriting] = useState(false);
  const [err, setErr] = useState(null);
  const notFinalized = decisions.filter((f) => f.change_status === 'not_finalized');
  const deptCounts = {};
  for (const f of [...decisions, ...codified]) for (const d of f.departments || []) deptCounts[d] = (deptCounts[d] || 0) + 1;
  const depts = Object.entries(deptCounts).sort((x, y) => y[1] - x[1]);
  const mainReport = (reports || []).find((r) => r.report_type === 'comprehensive' && r.status === 'completed');
  const execReport = (reports || []).find((r) => r.report_type === 'executive' && r.status === 'completed');
  const writeSummary = async () => {
    setWriting(true); setErr(null);
    try { const d = await post(`/api/priq/analyses/${a.id}/summary/refresh`); if (!d.success) throw new Error(d.message || 'The summary could not be written.'); await onSummaryWritten(); } catch (e) { setErr(e.message); } finally { setWriting(false); }
  };
  const items = c ? destinationRows(c) : [];
  const open = items.filter((it) => !it.done).length;

  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_320px] gap-10 px-8 py-8">
      <div className="max-w-3xl space-y-10">
        <section>
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3">Summary</h2>
          {rs ? (
            <>
              <p className="text-[15px] text-slate-800 leading-7">{rs.overview}</p>
              <p className="text-sm text-slate-500 mt-3">{decisions.length} decisions across {(rs.topics || []).length} topics · {codified.length} sections of the regulation changed{notFinalized.length ? ` · ${notFinalized.length} proposal${notFinalized.length === 1 ? '' : 's'} not finalized` : ''}</p>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 p-5">
              <p className="text-sm text-slate-600">This rule was read before summaries existed. Its decisions and regulation changes are here; the summary can be written now from what was read.</p>
              {canWork && <button onClick={writeSummary} disabled={writing} className={btn.primary + ' mt-3'}>{writing ? 'Writing the summary… (a few minutes)' : 'Write the summary'}</button>}
              {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
            </div>
          )}
        </section>

        {notFinalized.length > 0 && (
          <section>
            <h2 className="text-[15px] font-semibold text-slate-900 mb-3">What CMS did not finalize</h2>
            <ul className="divide-y divide-slate-100 border-y border-slate-100">
              {notFinalized.map((f) => (
                <li key={f.id} className="py-3">
                  <div className="text-sm text-slate-900">{cleanTitle(f.title)}</div>
                  <div className="text-sm text-slate-500 italic mt-0.5">“{f.current_year_text}”</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {rs && (
          <section>
            <h2 className="text-[15px] font-semibold text-slate-900 mb-1">Topic by topic</h2>
            <p className="text-xs text-slate-400 mb-3">Open a topic to read it and see CMS’s own words.</p>
            <div className="border-y border-slate-100 divide-y divide-slate-100">
              {(rs.topics || []).map((t) => <TopicRow key={t.path} t={t} byId={byId} />)}
            </div>
          </section>
        )}
      </div>

      <aside className="space-y-8 xl:sticky xl:top-6 self-start">
        <div className="rounded-lg border border-orange-200 p-4">
          <div className="text-[13px] font-semibold text-slate-900 mb-3">Policies</div>
          {!cov ? <div className="text-sm text-slate-400">Loading…</div> : (() => {
            const homes = cov.homes || [];
            const covered = homes.filter((h) => h.status === 'covered').length;
            const uncovered = homes.filter((h) => h.status === 'uncovered').length;
            const unsure = homes.filter((h) => h.status === 'unsure').length;
            return homes.length === 0 ? (
              <p className="text-sm text-slate-500">This rule has not been placed on the policies yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="flex-1 text-slate-700">Covered by a policy</span><span className="text-slate-500 tabular-nums">{covered}</span></li>
                <li className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${uncovered ? 'bg-orange-500' : 'bg-slate-200'}`} /><span className="flex-1 text-slate-700">Need a policy</span><span className="text-slate-500 tabular-nums">{uncovered}</span></li>
                <li className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${unsure ? 'bg-amber-500' : 'bg-slate-200'}`} /><span className="flex-1 text-slate-700">Not sure where they go</span><span className="text-slate-500 tabular-nums">{unsure}</span></li>
              </ul>
            );
          })()}
          <button onClick={() => onView('policies')} className="text-sm text-orange-700 hover:underline mt-3">See every requirement</button>
        </div>

        <div className="rounded-lg border border-slate-200 p-4">
          <div className="text-[13px] font-semibold text-slate-900 mb-3">Where the work went</div>
          {!c ? <div className="text-sm text-slate-400">Loading…</div> : (
            <ul className="space-y-2 text-sm">
              {DESTINATIONS.map((d) => {
                const rows = items.filter((it) => it.dest === d.key);
                return (
                  <li key={d.key} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${rows.length ? d.dot : 'bg-slate-200'}`} />
                    <span className="flex-1 text-slate-700">{d.label}</span>
                    <span className="text-slate-500 tabular-nums">{rows.length ? `${rows.filter((r) => !r.done).length} open · ${rows.length}` : '0'}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <button onClick={() => onView('work')} className="text-sm text-orange-700 hover:underline mt-3">{open ? `${open} open items` : 'See the list'}</button>
        </div>

        {depts.length > 0 && (
          <div>
            <div className="text-[13px] font-semibold text-slate-900 mb-3">Departments involved</div>
            <ul className="space-y-2">
              {depts.slice(0, 8).map(([d, n]) => <li key={d} className="flex items-center gap-2.5 text-sm"><Avatar name={d} /><span className="flex-1 text-slate-700 truncate">{d}</span><span className="text-xs text-slate-400 tabular-nums">{n}</span></li>)}
            </ul>
          </div>
        )}

        <div>
          <div className="text-[13px] font-semibold text-slate-900 mb-3">Reports</div>
          <ul className="space-y-2">
            {[mainReport && { r: mainReport, label: 'Full summary' }, execReport && { r: execReport, label: 'Executive brief' }].filter(Boolean).map(({ r, label }) => (
              <li key={r.id}><button onClick={() => onDownload(r)} className="w-full flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 text-left"><span className="w-8 h-8 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center text-[10px] font-bold">PDF</span><span className="flex-1 min-w-0"><span className="block text-sm text-slate-800 truncate">{label}</span><span className="block text-xs text-slate-400">{a.current_year} · your organization’s design</span></span></button></li>
            ))}
          </ul>
          <button onClick={() => onView('reports')} className="text-sm text-orange-700 hover:underline mt-3">All reports</button>
        </div>
      </aside>
    </div>
  );
}

function TopicRow({ t, byId }) {
  const [open, setOpen] = useState(false);
  const rows = (t.decision_ids || []).map((id) => byId.get(id)).filter(Boolean);
  const secs = [...new Set((t.codified_ids || []).map((id) => byId.get(id)).filter(Boolean).map((f) => baseOf(f.cfr_section)).filter(Boolean))];
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left flex items-center gap-3 py-2.5 px-1 hover:bg-slate-50 rounded-md">
        <span className={`text-slate-400 text-xs w-3 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        <span className="flex-1 min-w-0"><span className="block text-sm text-slate-900 truncate">{t.title}</span>{t.area && <span className="block text-xs text-slate-400 truncate">{t.area}</span>}</span>
        {rows.length > 0 && <span className="text-xs text-slate-400 tabular-nums">{rows.length}</span>}
      </button>
      {open && (
        <div className="pl-7 pr-2 pb-4">
          <p className="text-sm text-slate-800 leading-6">{t.text}</p>
          {secs.length > 0 && <p className="text-xs text-slate-400 mt-2">Regulation: {secs.map((s) => `§ ${s}`).join(', ')}</p>}
          {rows.length > 0 && (
            <ul className="mt-3 space-y-2.5 border-l-2 border-slate-100 pl-4">
              {rows.map((f) => <li key={f.id}><div className="text-sm text-slate-800">{cleanTitle(f.title)}{f.change_status === 'not_finalized' && <span className="text-rose-700"> · not finalized</span>}</div><div className="text-sm text-slate-500 italic">“{f.current_year_text}”</div></li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Where the work went: one list, sections per destination, task pane
// ---------------------------------------------------------------------------
const DESTINATIONS = [
  { key: 'policy', label: 'Policy app', dot: 'bg-sky-500', chip: 'blue' },
  { key: 'review', label: 'Policy reviews', dot: 'bg-violet-500', chip: 'purple' },
  { key: 'department', label: 'Departments', dot: 'bg-teal-500', chip: 'teal' },
  { key: 'issue', label: 'Issues log', dot: 'bg-amber-500', chip: 'yellow' },
  { key: 'risk', label: 'Risk assessment', dot: 'bg-rose-500', chip: 'red' },
  { key: 'cap', label: 'Corrective actions', dot: 'bg-orange-500', chip: 'orange' },
  { key: 'priority', label: 'Priority board', dot: 'bg-emerald-500', chip: 'green' },
];
const destOf = (key) => DESTINATIONS.find((d) => d.key === key) || DESTINATIONS[0];
const WORK_LABEL = { pending: 'To do', in_progress: 'In progress', blocked: 'Blocked', completed: 'Done', cancelled: 'Dropped' };
const nice = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase());
const DONE = new Set(['completed', 'closed', 'resolved', 'dismissed', 'cancelled', 'actioned', 'accepted', 'transferred']);

// Every item the rule sent somewhere, as one row shape for the list.
function destinationRows(c) {
  const rows = [];
  for (const p of c.policies || []) {
    const reviews = (c.reconciliation || []).filter((r) => r.policy_id === p.policy_id);
    rows.push({ id: `policy-${p.policy_id}`, dest: 'policy', title: `${p.policy_number} · ${p.title}`, owner: p.department, status: reviews.length ? `${reviews.length} review${reviews.length === 1 ? '' : 's'}` : 'Reviewed', done: reviews.length === 0, note: `Cites ${p.sections.map((s) => `§ ${s}`).join(', ')}. The policy app compares it with the rule and raises a review where they conflict.`, link: '/app?app=ppm', linkLabel: 'Open the policy app' });
  }
  for (const r of c.reconciliation || []) rows.push({ id: `review-${r.id}`, dest: 'review', title: `${r.policy_number ? `${r.policy_number} · ` : ''}${r.policy_title || 'Policy'}${r.topic ? ` · ${r.topic}` : ''}`, owner: null, status: nice(r.status), done: DONE.has(r.status), date: r.created_at, note: r.summary, link: '/app?app=ppm', linkLabel: 'Open the policy app' });
  for (const w of c.work_items || []) rows.push({ id: `work-${w.id}`, raw: w, dest: 'department', title: w.title, owner: w.owner || w.department, status: WORK_LABEL[w.status] || nice(w.status), done: DONE.has(w.status), date: w.due_date, note: w.department ? `${w.department}${w.owner ? ` · ${w.owner}` : ''}` : 'No department yet' });
  for (const i of c.issues || []) rows.push({ id: `issue-${i.id}`, dest: 'issue', title: i.title, owner: i.owner || i.department, status: nice(i.status || 'open'), done: DONE.has(i.status), date: i.created_at, note: i.note, link: '/app?app=compliance_issues_log', linkLabel: 'Open the issues log' });
  for (const r of c.risks || []) rows.push({ id: `risk-${r.id}`, dest: 'risk', title: r.title, owner: r.owner || r.department, status: nice(r.status || 'open'), done: DONE.has(r.status), date: r.created_at, note: [r.category, r.note].filter(Boolean).join(' · '), link: '/app?app=risk', linkLabel: 'Open the risk assessment' });
  for (const x of c.corrective_actions || []) rows.push({ id: `cap-${x.id}`, dest: 'cap', title: x.title, owner: null, status: nice(x.status), done: DONE.has(x.status), date: x.due_date, link: `/app?app=cap&cap_id=${encodeURIComponent(x.id)}`, linkLabel: 'Open the corrective action' });
  for (const x of c.priority_items || []) rows.push({ id: `pri-${x.id}`, dest: 'priority', title: x.title, owner: null, status: nice(x.status), done: DONE.has(x.status), date: x.created_at, link: '/app?app=overmind', linkLabel: 'Open the priority board' });
  return rows;
}

function WorkList({ c, canWork, onOpen, onCreate }) {
  const [q, setQ] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  if (!c) return <Empty>Loading…</Empty>;
  const needle = q.trim().toLowerCase();
  const rows = destinationRows(c).filter((r) => (!onlyOpen || !r.done) && (!needle || `${r.title} ${r.owner || ''} ${r.status}`.toLowerCase().includes(needle)));
  const total = destinationRows(c).length;
  return (
    <div className="pb-10">
      <div className="px-8 py-3 border-b border-slate-200 flex items-center gap-3">
        {canWork && <button onClick={() => onCreate({})} className={btn.secondary}>+ Add action</button>}
        <label className={btn.ghost + ' cursor-pointer'}><input type="checkbox" className="accent-orange-600 mr-1.5" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />Open only</label>
        <div className="flex-1" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className={field + ' max-w-[220px]'} />
      </div>
      {total === 0 ? (
        <Empty>Nothing has gone anywhere yet. When policies cite a changed section, when the policy app raises a review, or when you create an action from this rule, it shows here.</Empty>
      ) : (
        <div className="overflow-x-auto"><div className="min-w-[780px]">
          <div className="grid grid-cols-[minmax(220px,1fr)_130px_160px_120px_100px] gap-3 px-8 py-2 text-xs font-medium text-slate-500 border-b border-slate-200">
            <div className="pl-7">Name</div><div>Sent to</div><div>Owner</div><div>Status</div><div>Date</div>
          </div>
          {DESTINATIONS.map((d) => {
            const list = rows.filter((r) => r.dest === d.key);
            if (!list.length) return null;
            const isCollapsed = !!collapsed[d.key];
            return (
              <div key={d.key}>
                <button onClick={() => setCollapsed((s) => ({ ...s, [d.key]: !s[d.key] }))} className="w-full text-left px-8 py-2 mt-2 flex items-center gap-2 hover:bg-slate-50">
                  <span className={`text-slate-400 text-[10px] transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>▶</span>
                  <span className="text-sm font-semibold text-slate-900">{d.label}</span>
                  <span className="text-xs text-slate-400 tabular-nums">{list.length}</span>
                </button>
                {!isCollapsed && list.map((r) => (
                  <button key={r.id} onClick={() => onOpen(r)} className="w-full text-left grid grid-cols-[minmax(220px,1fr)_130px_160px_120px_100px] gap-3 items-center px-8 py-1.5 border-b border-slate-100 hover:bg-slate-50 min-h-[40px]">
                    <div className="flex items-center gap-3 min-w-0"><CheckCircle done={r.done} /><span className={`text-sm truncate ${r.done ? 'text-slate-400' : 'text-slate-900'}`}>{r.title}</span></div>
                    <div><Chip tone={d.chip}>{d.label}</Chip></div>
                    <div className="flex items-center gap-2 min-w-0"><Avatar name={r.owner} /><span className="text-sm text-slate-600 truncate">{r.owner || ''}</span></div>
                    <div><Chip tone={r.done ? 'green' : /open|to do|pending|active/i.test(r.status) ? 'gray' : 'yellow'}>{r.status}</Chip></div>
                    <div className="text-sm text-slate-500">{fmtDate(r.date)}</div>
                  </button>
                ))}
              </div>
            );
          })}
        </div></div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task pane: one item
// ---------------------------------------------------------------------------
function Pane({ title, onClose, children, footer }) {
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  return (
    <aside className="w-[440px] shrink-0 border-l border-slate-200 bg-white flex flex-col">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between"><div className="text-xs text-slate-400">{title}</div><button onClick={onClose} className="text-slate-400 hover:text-slate-800 text-lg leading-none" aria-label="Close">×</button></div>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="px-5 py-3 border-t border-slate-100">{footer}</div>}
    </aside>
  );
}
const Field = ({ label, children }) => <div className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-3 py-1.5"><div className="text-xs text-slate-500">{label}</div><div className="text-sm text-slate-800 min-w-0">{children}</div></div>;

function ItemPane({ item, ruleId, canWork, onClose, onChanged }) {
  const d = destOf(item.dest);
  const [depts, setDepts] = useState([]);
  const w = item.raw;
  useEffect(() => { if (item.dest === 'department' && canWork) j('/api/departments').then((x) => setDepts(Array.isArray(x) ? x : [])).catch(() => {}); }, [item.dest, canWork]);
  const patch = async (body) => { try { await j(`/api/priq/action-items/${w.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); onChanged(); } catch { /* ignore */ } };
  return (
    <Pane title={d.label} onClose={onClose} footer={item.link && <a href={item.link} className={btn.secondary}>{item.linkLabel || 'Open'}</a>}>
      <div className="flex items-start gap-3">
        <CheckCircle done={item.done} onClick={w && canWork ? () => patch({ status: w.status === 'completed' ? 'pending' : 'completed' }) : undefined} />
        <h3 className="text-[17px] font-semibold text-slate-900 leading-snug">{item.title}</h3>
      </div>
      <div className="mt-4 divide-y divide-slate-50">
        <Field label="Sent to"><Chip tone={d.chip}>{d.label}</Chip></Field>
        <Field label="Owner">
          {w && canWork ? (
            <select value={w.department_id || depts.find((x) => x.name === w.department)?.id || ''} onChange={(e) => patch({ department_id: e.target.value || null })} className={field}><option value="">Choose a department</option>{depts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
          ) : <span className="flex items-center gap-2"><Avatar name={item.owner} />{item.owner || <span className="text-slate-400">Nobody yet</span>}</span>}
        </Field>
        <Field label="Status">
          {w && canWork ? (
            <select value={w.status} onChange={(e) => patch({ status: e.target.value })} className={field}>{Object.entries(WORK_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          ) : <Chip tone={item.done ? 'green' : 'gray'}>{item.status}</Chip>}
        </Field>
        <Field label={w ? 'Due' : 'Date'}>
          {w && canWork ? <input type="date" value={w.due_date ? String(w.due_date).slice(0, 10) : ''} onChange={(e) => patch({ due_date: e.target.value || null })} className={field} /> : (fmtDate(item.date) || <span className="text-slate-400">None</span>)}
        </Field>
      </div>
      {item.note && <div className="mt-5"><div className="text-xs text-slate-500 mb-1">Notes</div><p className="text-sm text-slate-800 leading-6 whitespace-pre-wrap">{item.note}</p></div>}
    </Pane>
  );
}

// ---------------------------------------------------------------------------
// Create pane: send an action from the rule to a department, the issues log
// or the risk assessment
// ---------------------------------------------------------------------------
function CreatePane({ ruleId, draft, onClose, onCreated }) {
  const [kind, setKind] = useState(draft.kind || 'department');
  const [title, setTitle] = useState(draft.title || '');
  const [note, setNote] = useState(draft.note || '');
  const [departmentId, setDepartmentId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [domainId, setDomainId] = useState('');
  const [depts, setDepts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { j('/api/departments').then((x) => setDepts(Array.isArray(x) ? x : [])).catch(() => {}); }, []);
  useEffect(() => { if (kind === 'risk' && !domains.length) j('/api/risk-assessment/domains').then((d) => setDomains(d.success ? d.domains || [] : [])).catch(() => {}); }, [kind, domains.length]);
  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const d = await post(`/api/priq/analyses/${ruleId}/actions`, { kind, title, note, department_id: departmentId || null, due_date: dueDate || null, domain_id: domainId || null, question: draft.question || null, answer: draft.answer || null });
      if (!d.success) throw new Error(d.message || 'The action could not be created.');
      const dest = kind; const dept = depts.find((x) => x.id === departmentId)?.name || null;
      onCreated({ id: `${dest}-new-${d.target_id}`, dest, title, owner: dept, status: kind === 'department' ? 'To do' : 'Open', done: false, date: dueDate || new Date().toISOString(), note });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const kinds = [['department', 'A department'], ['issue', 'Issues log'], ['risk', 'Risk assessment']];
  return (
    <Pane title="New action from this rule" onClose={onClose} footer={<div className="flex items-center gap-2"><button onClick={create} disabled={busy || !title.trim() || (kind === 'risk' && !domainId)} className={btn.primary}>{busy ? 'Creating…' : 'Create'}</button><button onClick={onClose} className={btn.ghost}>Cancel</button>{err && <span className="text-xs text-rose-600">{err}</span>}</div>}>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to happen" className="w-full text-[17px] font-semibold text-slate-900 placeholder-slate-300 focus:outline-none" autoFocus />
      <div className="mt-4">
        <div className="text-xs text-slate-500 mb-1.5">Send to</div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5">{kinds.map(([k, l]) => <button key={k} onClick={() => setKind(k)} className={`px-3 py-1 rounded text-sm ${kind === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>)}</div>
      </div>
      <div className="mt-3 divide-y divide-slate-50">
        <Field label={kind === 'department' ? 'Department' : 'Department'}><select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className={field}><option value="">{kind === 'department' ? 'Choose a department' : 'Optional'}</option>{depts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></Field>
        {kind === 'department' && <Field label="Due"><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={field} /></Field>}
        {kind === 'risk' && <Field label="Category"><select value={domainId} onChange={(e) => setDomainId(e.target.value)} className={field}><option value="">Choose a category</option>{domains.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></Field>}
      </div>
      <div className="mt-4"><div className="text-xs text-slate-500 mb-1">Notes</div><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={8} placeholder="Why, and what good looks like" className={field + ' leading-6'} /></div>
      {draft.question && <p className="text-xs text-slate-400 mt-3">From your question: “{draft.question}”</p>}
    </Pane>
  );
}

// ---------------------------------------------------------------------------
// Ask the rule
// ---------------------------------------------------------------------------
function Ask({ ruleId, onCreate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { j(`/api/priq/analyses/${ruleId}/chat`).then((d) => { if (d.success) setMessages(d.messages || []); }).catch(() => {}); }, [ruleId]);
  useEffect(() => {
    // Scroll the conversation pane only, never the page around it.
    const el = endRef.current; const sc = el && el.closest('main');
    if (sc) sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
  }, [messages]);
  const send = async (text) => {
    const msg = (text || input).trim(); if (!msg || sending) return;
    setInput(''); setSending(true);
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    try { const d = await post(`/api/priq/analyses/${ruleId}/chat`, { message: msg }); if (d.success) setMessages((m) => [...m, { role: 'assistant', content: d.reply }]); } catch { /* ignore */ }
    setSending(false);
  };
  const questionBefore = (i) => { for (let k = i - 1; k >= 0; k--) if (messages[k].role === 'user') return messages[k].content; return null; };
  const suggestions = ['What did CMS decide not to finalize?', 'What changes for Star Ratings?', 'What changed for marketing, agents and brokers?', 'Which Part D changes apply and when?', 'What must Compliance do first?'];
  return (
    <div className="max-w-3xl px-8 py-6 flex flex-col" style={{ minHeight: '100%' }}>
      <div className="flex-1 space-y-6">
        {messages.length === 0 && (
          <div className="py-6">
            <p className="text-sm text-slate-600 mb-3">Ask anything about this rule. Answers quote CMS’s own words, and any answer can become an action for a department, the issues log or the risk assessment.</p>
            <div className="flex flex-wrap gap-2">{suggestions.map((s) => <button key={s} onClick={() => send(s)} className="text-sm border border-slate-200 rounded-full px-3 py-1.5 text-slate-700 hover:bg-slate-50">{s}</button>)}</div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="flex gap-3">
            {m.role === 'user' ? <Avatar name="You" size={7} /> : <span className="w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold shrink-0">§</span>}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-slate-400 mb-1">{m.role === 'user' ? 'You' : 'RuleIQ'}</div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-6">{m.content}</p>
              {m.role === 'assistant' && (
                <button onClick={() => onCreate({ title: (questionBefore(i) || '').replace(/\?$/, '').slice(0, 120), note: m.content, question: questionBefore(i), answer: m.content })} className={btn.secondary + ' mt-2'}>Create action</button>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="text-xs text-slate-400 animate-pulse pl-10">Reading the rule…</div>}
        <div ref={endRef} />
      </div>
      <div className="mt-6 flex gap-2 sticky bottom-0 bg-white py-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask about this rule" className={field} disabled={sending} />
        <button onClick={() => send()} disabled={sending || !input.trim()} className={btn.primary}>Ask</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// All changes, in full: every regulation section changed and every decision
// CMS states, each with its explanation and CMS's own words. Nothing cut off.
// ---------------------------------------------------------------------------
const CHANGE_LABEL = { new: ['Added', 'green'], modified: ['Changed', 'yellow'], removed: ['Removed', 'gray'], clarified: ['Finalized', 'green'], not_finalized: ['Not finalized', 'red'], withdrawn: ['Not finalized', 'red'], delayed: ['Delayed', 'yellow'] };
function Changes({ decisions, codified, rs }) {
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const topicOf = (id) => (rs?.topics || []).find((t) => (t.decision_ids || []).includes(id) || (t.codified_ids || []).includes(id));
  const sortSec = (a, b) => String(a.cfr_section || '').localeCompare(String(b.cfr_section || ''), undefined, { numeric: true });
  const reg = [...codified].sort(sortSec);
  const dec = [...decisions];
  const nf = dec.filter((f) => f.change_status === 'not_finalized');
  const all = kind === 'regulation' ? reg : kind === 'decisions' ? dec : kind === 'not_finalized' ? nf : [...reg, ...dec];
  const rows = all.filter((f) => !needle || `${f.title} ${f.summary} ${f.cfr_section || ''} ${f.current_year_text || ''}`.toLowerCase().includes(needle));
  const filters = [['all', 'All', reg.length + dec.length], ['regulation', 'Regulation text', reg.length], ['decisions', 'CMS decisions', dec.length], ['not_finalized', 'Not finalized', nf.length]];
  return (
    <div className="pb-12">
      <div className="px-8 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap sticky top-0 bg-white z-10">
        {filters.map(([id, label, n]) => <button key={id} onClick={() => setKind(id)} className={`px-3 py-1.5 rounded-full text-sm ${kind === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{label} <span className="opacity-60 tabular-nums">{n}</span></button>)}
        <div className="flex-1" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search changes" className={field + ' max-w-[240px]'} />
      </div>
      <div className="px-8">
        {rows.length === 0 ? <Empty>No changes match.</Empty> : rows.map((f) => {
          const isReg = f.pass === 'pass_amendatory';
          const [label, tone] = CHANGE_LABEL[f.change_status] || CHANGE_LABEL.clarified;
          const sec = (String(f.cfr_section || '').match(/\b\d{2,3}\.\d+[\w().]*/) || [])[0];
          const t = topicOf(f.id);
          return (
            <article key={f.id} className="py-6 border-b border-slate-100 max-w-4xl">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Chip tone={isReg ? 'blue' : 'purple'}>{isReg ? 'Regulation text' : 'CMS decision'}</Chip>
                <Chip tone={tone}>{label}</Chip>
                {sec && <span className="text-xs text-slate-500">42 CFR § {sec}</span>}
                {t && <span className="text-xs text-slate-400">· {t.title}</span>}
              </div>
              <h3 className="text-[16px] font-semibold text-slate-900">{cleanTitle(f.title)}</h3>
              {f.summary && <p className="text-sm text-slate-800 leading-6 mt-2 whitespace-pre-wrap">{f.summary}</p>}
              {f.current_year_text && (
                <div className="mt-3 border-l-2 border-slate-200 pl-4">
                  <div className="text-xs text-slate-500 mb-1">{isReg ? 'The instruction, as published' : 'In CMS’s words'}</div>
                  <p className="text-sm text-slate-700 italic leading-6 whitespace-pre-wrap">“{f.current_year_text}”</p>
                </div>
              )}
              {(f.departments || []).length > 0 && <div className="mt-3 flex items-center gap-1.5 flex-wrap">{f.departments.map((d) => <Chip key={d}>{d}</Chip>)}</div>}
            </article>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------
function Reports({ reports, canWork, ruleId, onDownload, onRebuilt }) {
  const [busy, setBusy] = useState(false);
  const rebuild = async () => { setBusy(true); try { await post(`/api/priq/analyses/${ruleId}/reports/refresh`); await onRebuilt(); } catch { /* ignore */ } finally { setBusy(false); } };
  if (!reports) return <Empty>Loading…</Empty>;
  if (!reports.length) return <Empty>Reports are being prepared.</Empty>;
  const main = reports.filter((r) => ['comprehensive', 'executive'].includes(r.report_type));
  const dept = reports.filter((r) => !['comprehensive', 'executive'].includes(r.report_type));
  const Card = ({ r }) => (
    <button onClick={() => r.status === 'completed' && onDownload(r)} className="text-left rounded-lg border border-slate-200 p-4 hover:bg-slate-50 hover:border-slate-300 transition flex items-start gap-3">
      <span className="w-9 h-9 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center text-[10px] font-bold shrink-0">PDF</span>
      <span className="min-w-0"><span className="block text-sm font-medium text-slate-900 truncate">{r.title || r.department}</span><span className="block text-xs text-slate-400 mt-0.5">{r.status === 'completed' ? `${Math.max(1, Math.round((r.file_size_bytes || 0) / 1024))} KB · Download` : r.status === 'failed' ? 'Could not prepare' : 'Preparing'}</span></span>
    </button>
  );
  return (
    <div className="px-8 py-8 max-w-4xl space-y-8">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Every report carries your organization’s name, logo and colors.</p>
        {canWork && <button onClick={rebuild} disabled={busy} className={btn.secondary}>{busy ? 'Rebuilding…' : 'Rebuild reports'}</button>}
      </div>
      <section><h2 className="text-[13px] font-semibold text-slate-900 mb-3">For the whole plan</h2><div className="grid sm:grid-cols-2 gap-3">{main.map((r) => <Card key={r.id} r={r} />)}</div></section>
      {dept.length > 0 && <section><h2 className="text-[13px] font-semibold text-slate-900 mb-3">By department</h2><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{dept.map((r) => <Card key={r.id} r={r} />)}</div></section>}
    </div>
  );
}
